import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ShoppingBag, User, Settings, ChevronRight, Gift, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/orders`, {
          withCredentials: true
        });
        setOrders(response.data);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    const fetchReferral = async () => {
      try {
        const response = await axios.get(`${API}/referral/me`, { withCredentials: true });
        setReferral(response.data);
      } catch (error) {
        console.error('Error fetching referral info:', error);
      }
    };
    fetchOrders();
    fetchReferral();
  }, []);

  const referralLink = referral ? `${window.location.origin}/register?ref=${referral.referral_code}` : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-forest/10 text-forest';
      case 'pending': return 'bg-gold/20 text-brown';
      case 'delivered': return 'bg-forest text-cream';
      default: return 'bg-dark/10 text-dark';
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-cream" data-testid="dashboard-page">
      <div className="container-custom py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-forest rounded-full flex items-center justify-center">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
              ) : (
                <User className="w-8 h-8 text-cream" />
              )}
            </div>
            <div>
              <h1 className="font-playfair text-3xl font-bold text-dark" data-testid="dashboard-title">
                Welcome, {user?.name?.split(' ')[0]}!
              </h1>
              <p className="font-manrope text-dark/60">{user?.email}</p>
            </div>
          </div>
          <Link to="/shop" className="btn-primary" data-testid="dashboard-shop-btn">
            <ShoppingBag className="w-5 h-5 mr-2" />
            Continue Shopping
          </Link>
        </div>

        {/* Content */}
        <Tabs defaultValue="orders" className="space-y-8">
          <TabsList className="bg-white border border-forest/10 p-1 rounded-full">
            <TabsTrigger value="orders" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-cream">
              <Package className="w-4 h-4 mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="refer" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-cream" data-testid="dashboard-refer-tab">
              <Gift className="w-4 h-4 mr-2" />
              Refer & Earn
            </TabsTrigger>
            <TabsTrigger value="account" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-cream">
              <Settings className="w-4 h-4 mr-2" />
              Account
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="bg-white rounded-2xl border border-forest/10 overflow-hidden" data-testid="orders-list">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-forest"></div>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-dark/20 mx-auto mb-4" />
                  <h3 className="font-playfair text-xl font-bold text-dark mb-2">No orders yet</h3>
                  <p className="font-manrope text-dark/60 mb-6">Start shopping to see your orders here.</p>
                  <Link to="/shop" className="btn-primary">
                    Browse Boxes
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-forest/10">
                  {orders.map((order) => (
                    <div key={order.order_id} className="p-6 hover:bg-cream/50 transition-colors" data-testid={`order-${order.order_id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                        <div>
                          <p className="font-manrope font-medium text-dark">{order.order_id}</p>
                          <p className="text-sm text-dark/60">{new Date(order.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                          <span className="font-playfair text-xl font-bold text-dark">
                            ${order.total.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {order.items.slice(0, 3).map((item, index) => (
                          <span key={index} className="text-sm text-dark/60 font-manrope">
                            {item.name} x{item.quantity}
                            {index < Math.min(order.items.length, 3) - 1 && ','}
                          </span>
                        ))}
                        {order.items.length > 3 && (
                          <span className="text-sm text-dark/60 font-manrope">+{order.items.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Refer & Earn Tab */}
          <TabsContent value="refer">
            <div className="bg-white rounded-2xl p-8 border border-forest/10 mb-6" data-testid="referral-card">
              <div className="flex items-center gap-3 mb-2">
                <Gift className="w-6 h-6 text-forest" />
                <h2 className="font-playfair text-2xl font-bold text-dark">Refer & Earn</h2>
              </div>
              <p className="font-manrope text-dark/60 mb-6">
                Share your link. When a friend subscribes (weekly, bi-weekly, or monthly) and completes their first order, you get 10% off your next order plus a free small local product &mdash; as long as you currently have an active subscription yourself.
              </p>
              {referral ? (
                <>
                  <div className="flex flex-col sm:flex-row gap-3 mb-2">
                    <div className="flex-grow bg-cream border border-forest/20 rounded-xl px-4 py-3 font-manrope text-dark truncate" data-testid="referral-link-display">
                      {referralLink}
                    </div>
                    <button onClick={handleCopy} className="btn-primary justify-center px-6" data-testid="copy-referral-link-btn">
                      {copied ? <Check className="w-5 h-5 mr-2" /> : <Copy className="w-5 h-5 mr-2" />}
                      {copied ? 'Copied' : 'Copy Link'}
                    </button>
                  </div>
                  <p className="text-sm text-dark/50 font-manrope mb-6">Your code: <span className="font-bold text-forest" data-testid="referral-code-display">{referral.referral_code}</span> &middot; {referral.referred_count} friend{referral.referred_count === 1 ? '' : 's'} referred</p>

                  <h3 className="font-manrope font-bold text-dark mb-3">Your Rewards</h3>
                  {referral.rewards.length === 0 ? (
                    <p className="text-sm text-dark/50 font-manrope" data-testid="no-rewards-message">No referral rewards yet &mdash; share your link to get started!</p>
                  ) : (
                    <div className="space-y-2" data-testid="referral-rewards-list">
                      {referral.rewards.map((r) => (
                        <div key={r.reward_id} className="flex items-center justify-between bg-cream rounded-xl px-4 py-3" data-testid={`referral-reward-${r.reward_id}`}>
                          <span className="font-manrope text-sm text-dark">{r.referred_user_name} converted</span>
                          <span className={`text-xs font-bold uppercase px-3 py-1 rounded-full ${r.status === 'awarded' ? 'bg-gold/20 text-brown' : 'bg-forest/10 text-forest'}`}>
                            {r.status === 'awarded' ? 'Ready — applies at next checkout' : 'Redeemed'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex justify-center py-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest"></div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Account Tab */}
          <TabsContent value="account">
            <div className="bg-white rounded-2xl p-8 border border-forest/10" data-testid="account-info">
              <h2 className="font-playfair text-2xl font-bold text-dark mb-6">Account Information</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-4 border-b border-forest/10">
                  <div>
                    <p className="font-manrope font-medium text-dark">Name</p>
                    <p className="text-dark/60">{user?.name}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between py-4 border-b border-forest/10">
                  <div>
                    <p className="font-manrope font-medium text-dark">Email</p>
                    <p className="text-dark/60">{user?.email}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between py-4">
                  <div>
                    <p className="font-manrope font-medium text-dark">Member Since</p>
                    <p className="text-dark/60">January 2026</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
